import React from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import {
  formatMetric,
  macroAnalysisFigmaSegmentColors,
  type AnalysisData,
  type RingArcSegment,
} from './analysis.shared';
import { MACRO_CHART_COLORS } from '@/constants/macro-chart-colors';
import { NutritionReportTable } from './NutritionReportTable';
import type { NutritionalAnalysisMicrosView } from './mergeMealPlanNutritionalAnalysis';
import type { Client } from '@/types/client';

const MACRO_LEGEND_FIGMA = [
  { key: 'carbs' as const, label: 'Carbs', color: macroAnalysisFigmaSegmentColors.carbs },
  { key: 'protein' as const, label: 'Protein', color: macroAnalysisFigmaSegmentColors.protein },
  { key: 'fat' as const, label: 'Fat', color: macroAnalysisFigmaSegmentColors.fat },
] as const;
const MACRO_FIBRE_COLOR = '#016064';

/** Align nutrient-report PDF with `NutritionReportSectionTable` (`hideExpandColumn`). */
const pdfMacroSectionHeaderMinHeight = 38;
const pdfMacroTableHeaderHeight = 36;
const pdfMacroTableBodyHeight = 38;
const pdfMacroHeaderCellPadding = '0 16px';
const pdfMacroBodyCellPadding = '0px 16px 12px 16px';
const pdfMacroHeaderCellSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.75rem',
  fontWeight: 700,
  color: '#98A2B3',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  borderBottomColor: '#E5E7EB',
  whiteSpace: 'nowrap' as const,
};

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function buildPieRingSegments(
  parts: Array<{ value: number; color: string }>,
  gapAngle = 12,
): RingArcSegment[] {
  const center = 50;
  const radius = 42;
  const normalized = parts.filter((part) => part.value > 0);
  const total = normalized.reduce((sum, part) => sum + part.value, 0);
  if (!(total > 0)) return [];

  let angle = -90;
  return normalized.map((part) => {
    const sweep = (part.value / total) * 360;
    const safeSweep = Math.max(0, sweep - gapAngle);
    const start = angle + gapAngle / 2;
    const end = start + safeSweep;
    const startPoint = polarToCartesian(center, center, radius, start);
    const endPoint = polarToCartesian(center, center, radius, end);
    const largeArc = safeSweep > 180 ? 1 : 0;
    angle += sweep;
    return {
      color: part.color,
      path: `M ${startPoint.x} ${startPoint.y} A ${radius} ${radius} 0 ${largeArc} 1 ${endPoint.x} ${endPoint.y}`,
    };
  });
}

/** Figma 97:5687 — analysis Macros tab (presentation only). */
export type MacronutrientAnalysisSectionAppearance = 'default' | 'macroFigma';

export interface MacronutrientMacroTabSurfaceHeaderProps {
  onExportReport: () => void;
  exportReportLoading?: boolean;
}

/** Title + subtitle + export for Macros tab; also used inside the section when `hideSurfaceHeader` is false. */
export function MacronutrientMacroTabSurfaceHeader({
  onExportReport,
  exportReportLoading = false,
}: MacronutrientMacroTabSurfaceHeaderProps) {
  return (
    <Box
      sx={{
        border: '1px solid #D0D5DD',
        borderRadius: '20px',
        bgcolor: '#fff',
        px: { xs: 1.5, sm: 2.25 },
        py: { xs: 1.2, sm: 1.4 },
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: 'minmax(0, 1fr) auto',
        },
        gap: 1.25,
        alignItems: 'center',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: '1.2rem', sm: '1.05rem' },
            fontWeight: 700,
            color: '#1D2939',
            lineHeight: 1.2,
            overflowWrap: 'anywhere',
          }}
        >
          Macronutrient Analysis
        </Typography>
        <Typography
          sx={{
            mt: 0.45,
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.75rem',
            color: '#667085',
            lineHeight: 1.35,
            overflowWrap: 'anywhere',
          }}
        >
          Intake breakdown by macronutrient, per kg body-weight, and energy contribution
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.2,
          justifyContent: { xs: 'flex-start', lg: 'flex-end' },
        }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={onExportReport}
          disabled={exportReportLoading}
          startIcon={
            exportReportLoading ? (
              <CircularProgress size={16} thickness={5} sx={{ color: 'currentColor' }} />
            ) : (
              <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
            )
          }
          sx={{
            height: 40,
            minWidth: 126,
            borderRadius: '14px',
            bgcolor: '#20AEDC',
            color: '#fff',
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#199BC5', boxShadow: 'none' },
            '&.Mui-disabled': {
              bgcolor: '#9fd7ea',
              color: '#fff',
            },
          }}
        >
          {exportReportLoading ? 'Exporting...' : 'Export'}
        </Button>
      </Box>
    </Box>
  );
}

interface MacronutrientAnalysisSectionProps {
  analysisData: AnalysisData;
  macroRingSegments: RingArcSegment[];
  tableVariant?: 'default' | 'nutrition-report';
  clientDisplayName?: string;
  subtitleParts?: string[];
  appearance?: MacronutrientAnalysisSectionAppearance;
  microsView?: NutritionalAnalysisMicrosView | null;
  /** Pass-through for Nutrient Report row food breakdown. */
  nutritionalApiResponse?: unknown;
  viewMode?: number;
  selectedDayIndex?: number;
  mealPlanId?: string;
  onExportReport?: () => void;
  exportReportLoading?: boolean;
  isPdfExport?: boolean;
  pdfMacroDistributionBlock?: React.ReactNode;
  /** Used for Nutrient Report macro Limits (AMDR from `client.drvRequirements`). */
  client?: Client;
  /** PDF: same nutrient-report layout; `macros-only` drops vitamin/mineral sections (Macros tab export). */
  pdfExportVariant?: 'full' | 'macros-only';
  /** When true (Macros tab with header rendered above KPI cards), omit the duplicate surface header here. */
  hideSurfaceHeader?: boolean;
  /** Nutrient Report: keys from nutritional-analysis `hiddenNutrients`. */
  hiddenNutrientKeys?: ReadonlySet<string>;
  /** Nutrient Report: POST hide/unhide then refetch analysis. */
  onNutrientVisibilityChange?: (args: { nutrientKey: string; hide: boolean }) => void | Promise<void>;
}

export const MacronutrientAnalysisSection: React.FC<MacronutrientAnalysisSectionProps> = ({
  analysisData,
  macroRingSegments,
  tableVariant = 'default',
  clientDisplayName,
  subtitleParts = [],
  appearance = 'default',
  microsView = null,
  nutritionalApiResponse,
  viewMode = 0,
  selectedDayIndex = 0,
  mealPlanId = '',
  onExportReport,
  exportReportLoading = false,
  isPdfExport = false,
  pdfMacroDistributionBlock,
  client,
  pdfExportVariant = 'full',
  hideSurfaceHeader = false,
  hiddenNutrientKeys,
  onNutrientVisibilityChange,
}) => {
  const isNutritionReport = tableVariant === 'nutrition-report';
  const isMacroFigma = appearance === 'macroFigma';
  const isPdfTableStyle = isPdfExport;
  const alcoholKpi = analysisData.kpis.find((item) => item.label.trim().toLowerCase() === 'alcohol');
  const alcoholPercent = React.useMemo(() => {
    const fromApi = analysisData.macroDistributionPercent?.alcohol;
    if (fromApi != null && Number.isFinite(fromApi) && fromApi > 0) return fromApi;
    if (alcoholKpi && alcoholKpi.value > 0 && analysisData.energy > 0) {
      return ((alcoholKpi.value * 7) / analysisData.energy) * 100;
    }
    return 0;
  }, [alcoholKpi, analysisData.energy, analysisData.macroDistributionPercent?.alcohol]);
  const macroPieLegendItems = React.useMemo(() => {
    const base = [
      { key: 'carbs', label: 'Carbs', color: macroAnalysisFigmaSegmentColors.carbs, percent: analysisData.macroShare.carbs },
      { key: 'protein', label: 'Protein', color: macroAnalysisFigmaSegmentColors.protein, percent: analysisData.macroShare.protein },
      { key: 'fat', label: 'Fat', color: macroAnalysisFigmaSegmentColors.fat, percent: analysisData.macroShare.fat },
    ];
    if (alcoholPercent > 0) {
      base.push({ key: 'alcohol', label: 'Alcohol', color: MACRO_CHART_COLORS.alcohol, percent: alcoholPercent });
    }
    return base;
  }, [alcoholPercent, analysisData.macroShare.carbs, analysisData.macroShare.fat, analysisData.macroShare.protein]);
  const ringSegmentsForPie = React.useMemo(() => {
    if (alcoholPercent <= 0) return macroRingSegments;
    return buildPieRingSegments(macroPieLegendItems.map((item) => ({ value: item.percent, color: item.color })));
  }, [alcoholPercent, macroRingSegments, macroPieLegendItems]);
  const macroColumns = React.useMemo(() => {
    const totalSugar = analysisData.mealPlanNutritionReportDetail?.sugar;
    const sugarForMacroTable =
      totalSugar != null && Number.isFinite(totalSugar) && totalSugar >= 0
        ? totalSugar
        : analysisData.freeSugar;
    const sugarGramsPerKg =
      totalSugar != null && Number.isFinite(totalSugar) && totalSugar >= 0
        ? analysisData.gramsPerKgBodyWeight?.sugar != null && analysisData.gramsPerKgBodyWeight.sugar > 0
          ? formatMetric(analysisData.gramsPerKgBodyWeight.sugar, 1)
          : formatMetric(sugarForMacroTable / analysisData.bodyWeight, 1)
        : formatMetric(sugarForMacroTable / analysisData.bodyWeight, 1);
    const baseColumns = [
      {
        key: 'carbohydrate',
        label: 'Carbohydrate',
        intake: `${analysisData.carbs}g`,
        gramsPerKg: analysisData.gramsPerKgBodyWeight?.carbs != null && analysisData.gramsPerKgBodyWeight.carbs > 0
          ? formatMetric(analysisData.gramsPerKgBodyWeight.carbs, 1)
          : formatMetric(analysisData.carbs / analysisData.bodyWeight, 1),
        kilocal: formatMetric(analysisData.carbsKcal, 0),
        kilocalPercent: `${formatMetric(analysisData.macroShare.carbs, 1)}%`,
      },
      {
        key: 'sugars',
        label: 'Sugars',
        intake: `${sugarForMacroTable}g`,
        gramsPerKg: sugarGramsPerKg,
        kilocal: formatMetric(analysisData.sugarKcal, 0),
        kilocalPercent: analysisData.macroDistributionPercent
          ? `${formatMetric(analysisData.macroDistributionPercent.sugar, 1)}%`
          : '—',
      },
      {
        key: 'fibre',
        label: 'Fibre',
        intake: `${analysisData.fibre}g`,
        gramsPerKg: analysisData.gramsPerKgBodyWeight?.fibre != null && analysisData.gramsPerKgBodyWeight.fibre > 0
          ? formatMetric(analysisData.gramsPerKgBodyWeight.fibre, 1)
          : formatMetric(analysisData.fibre / analysisData.bodyWeight, 1),
        kilocal: analysisData.fibreKcal > 0
          ? formatMetric(analysisData.fibreKcal, 0)
          : analysisData.energy > 0 &&
            analysisData.macroDistributionPercent?.dietaryFibre != null &&
            Number.isFinite(analysisData.macroDistributionPercent.dietaryFibre)
            ? formatMetric(
                (analysisData.energy * analysisData.macroDistributionPercent.dietaryFibre) / 100,
                0,
              )
            : '—',
        kilocalPercent:
          analysisData.macroDistributionPercent?.dietaryFibre != null &&
          Number.isFinite(analysisData.macroDistributionPercent.dietaryFibre)
            ? `${formatMetric(analysisData.macroDistributionPercent.dietaryFibre, 1)}%`
            : analysisData.fibreKcal > 0 && analysisData.energy > 0
              ? `${formatMetric((analysisData.fibreKcal / analysisData.energy) * 100, 1)}%`
              : '—',
      },
      {
        key: 'protein',
        label: 'Protein',
        intake: `${analysisData.protein}g`,
        gramsPerKg: analysisData.gramsPerKgBodyWeight?.protein != null && analysisData.gramsPerKgBodyWeight.protein > 0
          ? formatMetric(analysisData.gramsPerKgBodyWeight.protein, 1)
          : formatMetric(analysisData.protein / analysisData.bodyWeight, 1),
        kilocal: formatMetric(analysisData.proteinKcal, 0),
        kilocalPercent: `${formatMetric(analysisData.macroShare.protein, 1)}%`,
      },
      {
        key: 'fat',
        label: 'Fat',
        intake: `${analysisData.fat}g`,
        gramsPerKg: analysisData.gramsPerKgBodyWeight?.fat != null && analysisData.gramsPerKgBodyWeight.fat > 0
          ? formatMetric(analysisData.gramsPerKgBodyWeight.fat, 1)
          : formatMetric(analysisData.fat / analysisData.bodyWeight, 1),
        kilocal: formatMetric(analysisData.fatKcal, 0),
        kilocalPercent: `${formatMetric(analysisData.macroShare.fat, 1)}%`,
      },
      {
        key: 'sat-fat',
        label: 'Sat Fat',
        intake: `${analysisData.saturatedFat}g`,
        gramsPerKg: analysisData.gramsPerKgBodyWeight?.saturatedFat != null && analysisData.gramsPerKgBodyWeight.saturatedFat > 0
          ? formatMetric(analysisData.gramsPerKgBodyWeight.saturatedFat, 2)
          : formatMetric(analysisData.saturatedFat / analysisData.bodyWeight, 2),
        kilocal: formatMetric(analysisData.satFatKcal, 0),
        kilocalPercent: analysisData.macroDistributionPercent
          ? `${formatMetric(analysisData.macroDistributionPercent.saturatedFat, 1)}%`
          : '—',
      },
    ];

    const extraColumns = alcoholKpi
      ? [
          {
            key: 'alcohol',
            label: alcoholKpi.label,
            intake: `${formatMetric(alcoholKpi.value, 0)}${alcoholKpi.unit}`,
            gramsPerKg: formatMetric(alcoholKpi.value / Math.max(1, analysisData.bodyWeight), 1),
            kilocal: formatMetric(alcoholKpi.value * 7, 0),
            kilocalPercent: `${formatMetric(((alcoholKpi.value * 7) / Math.max(1, analysisData.energy)) * 100, 1)}%`,
          },
        ]
      : [];

    return [...baseColumns, ...extraColumns];
  }, [
    analysisData.bodyWeight,
    analysisData.carbs,
    analysisData.carbsKcal,
    analysisData.energy,
    analysisData.fat,
    analysisData.fibre,
    analysisData.fatKcal,
    analysisData.freeSugar,
    analysisData.gramsPerKgBodyWeight,
    analysisData.kpis,
    analysisData.macroDistributionPercent,
    analysisData.mealPlanNutritionReportDetail?.sugar,
    analysisData.macroShare.carbs,
    analysisData.macroShare.fat,
    analysisData.macroShare.protein,
    analysisData.protein,
    analysisData.proteinKcal,
    analysisData.satFatKcal,
    analysisData.saturatedFat,
    analysisData.sugarKcal,
  ]);

  const tableRows = React.useMemo(
    () => [
      { label: 'Intake', values: macroColumns.map((column) => column.intake) },
      { label: 'g/kg body-weight', values: macroColumns.map((column) => column.gramsPerKg) },
      { label: 'Kilocal', values: macroColumns.map((column) => column.kilocal) },
      { label: 'Kilocal %', values: macroColumns.map((column) => column.kilocalPercent) },
    ],
    [macroColumns]
  );

  if (isNutritionReport) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <NutritionReportTable
          analysisData={analysisData}
          microsView={microsView}
          client={client}
          clientDisplayName={clientDisplayName}
          subtitleParts={subtitleParts}
          nutritionalApiResponse={nutritionalApiResponse}
          viewMode={viewMode}
          selectedDayIndex={selectedDayIndex}
          mealPlanId={mealPlanId}
          onExportReport={onExportReport}
          exportReportLoading={exportReportLoading}
          isPdfExport={isPdfExport}
          pdfMacroDistributionBlock={pdfMacroDistributionBlock}
          pdfExportVariant={pdfExportVariant}
          hiddenNutrientKeys={hiddenNutrientKeys}
          onNutrientVisibilityChange={onNutrientVisibilityChange}
        />
      </Box>
    );
  }

  const sectionRootSx = {
    borderRadius: isPdfTableStyle ? '8px' : '14px',
    border: isPdfTableStyle ? '1px solid #E4E7EC' : '1px solid #f3f4f6',
    bgcolor: '#fff',
    p: isPdfTableStyle ? 0 : '21px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: isMacroFigma ? '16px' : '15px',
    /** PDF: `overflow: hidden` can clip the legend under the donut and confuse capture height for html2canvas. */
    ...(isPdfTableStyle ? { overflow: 'visible' } : {}),
  };

  return (
    <Box sx={sectionRootSx}>
      {isPdfTableStyle ? (
        <Box
          sx={{
            px: 2.2,
            pt: 0,
            pb: 1.5,
            minHeight: `${pdfMacroSectionHeaderMinHeight}px`,
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #EAECF0',
            bgcolor: '#EAF8FD',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.85rem',
              fontWeight: 700,
              lineHeight: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#0B89B7',
            }}
          >
            Macronutrient Analysis
          </Typography>
        </Box>
      ) : hideSurfaceHeader ? null : isMacroFigma && onExportReport ? (
        <MacronutrientMacroTabSurfaceHeader onExportReport={onExportReport} exportReportLoading={exportReportLoading} />
      ) : (
        <>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '18px',
              lineHeight: '27px',
              letterSpacing: '-0.44px',
              color: '#1e2939',
            }}
          >
            Macronutrient Analysis
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '16px',
              color: '#99a1af',
            }}
          >
            Intake breakdown by macronutrient, per kg body-weight, and energy contribution
          </Typography>
        </>
      )}

      <Box
        sx={{
          px: 0,
          pt: 0,
          pb: 0,
          display: 'flex',
          /** PDF: stacked column — table first, then donut + % legend (same DOM order as screen `xs`). */
          flexDirection: isPdfTableStyle ? 'column' : { xs: 'column', lg: 'row' },
          alignItems: isPdfTableStyle ? 'stretch' : { xs: 'stretch', lg: 'center' },
          gap: isPdfTableStyle ? 2 : isMacroFigma ? '20px' : '24px',
          width: '100%',
          minWidth: 0,
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            minWidth: 0,
            border: isPdfTableStyle ? 'none' : '1px solid #e5e7eb',
            borderRadius: isPdfTableStyle ? 0 : '14px',
            overflowX: 'auto',
            bgcolor: '#fff',
          }}
        >
          <Table
            size="small"
            sx={{
              minWidth: 650,
              ...(isPdfTableStyle ? { tableLayout: 'fixed', width: '100%' } : {}),
            }}
            aria-label="Macronutrient analysis summary"
          >
            <TableHead
              sx={{
                '& .MuiTableCell-head': isPdfTableStyle
                  ? {
                      bgcolor: '#fff',
                      borderBottom: '1px solid #E5E7EB',
                      py: 0,
                      height: pdfMacroTableHeaderHeight,
                      p: pdfMacroHeaderCellPadding,
                      lineHeight: 1,
                      verticalAlign: 'middle',
                    }
                  : {
                      bgcolor: '#f9fafb',
                      borderBottom: '1px solid #e5e7eb',
                      py: '10px',
                      height: 44,
                    },
              }}
            >
              <TableRow>
                <TableCell
                  sx={{
                    width: 144,
                    borderBottomColor: '#E5E7EB',
                    ...(isPdfTableStyle ? { ...pdfMacroHeaderCellSx, p: pdfMacroHeaderCellPadding } : {}),
                  }}
                />
                {macroColumns.map((column) => (
                  <TableCell
                    key={column.key}
                    align="center"
                    sx={{
                      ...(isPdfTableStyle
                        ? {
                            ...pdfMacroHeaderCellSx,
                            p: pdfMacroHeaderCellPadding,
                            textAlign: 'center',
                          }
                        : {
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '12px',
                            fontWeight: 600,
                            lineHeight: '14.3px',
                            color: '#6a7282',
                            letterSpacing: '1.12px',
                            textTransform: 'uppercase',
                            borderBottomColor: '#e5e7eb',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          }),
                    }}
                  >
                    {isMacroFigma ? column.label.toUpperCase() : column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {tableRows.map((row) => (
                <TableRow
                  key={row.label}
                  sx={
                    isPdfTableStyle
                      ? {
                          '& .MuiTableCell-root': {
                            height: pdfMacroTableBodyHeight,
                            p: pdfMacroBodyCellPadding,
                            verticalAlign: 'middle',
                            borderBottom: '1px solid #E5E7EB',
                          },
                        }
                      : undefined
                  }
                >
                  <TableCell
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: row.label === 'Kilocal %' ? 600 : 400,
                      lineHeight: '16px',
                      color: '#4a5565',
                      borderBottom: isPdfTableStyle ? undefined : '1px solid #f3f4f6',
                      whiteSpace: 'nowrap',
                      py: isPdfTableStyle ? undefined : '14px',
                      height: isPdfTableStyle ? undefined : 45,
                    }}
                  >
                    {row.label}
                  </TableCell>
                  {row.values.map((value, valueIndex) => (
                    <TableCell
                      key={`${row.label}-${valueIndex}`}
                      align="center"
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontSize: isPdfTableStyle ? '0.88rem' : '14px',
                        fontWeight: row.label === 'Kilocal %' ? 600 : 400,
                        lineHeight: isPdfTableStyle ? 1.25 : '20px',
                        letterSpacing: isPdfTableStyle ? undefined : '-0.15px',
                        color: '#1e2939',
                        borderBottom: isPdfTableStyle ? undefined : '1px solid #f3f4f6',
                        whiteSpace: 'nowrap',
                        textAlign: 'center',
                        py: isPdfTableStyle ? undefined : '14px',
                        height: isPdfTableStyle ? undefined : 45,
                      }}
                    >
                      {value}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            display: 'flex',
            flexDirection: isPdfTableStyle ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: isPdfTableStyle ? 'flex-start' : undefined,
            gap: isPdfTableStyle ? '32px' : '10.5px',
            width: isPdfTableStyle ? '100%' : { xs: '100%', lg: 158 },
            flexShrink: 0,
            justifySelf: { xs: 'center', lg: 'auto' },
            ...(isPdfTableStyle
              ? { alignSelf: 'stretch', px: 2, pb: 2, pt: 1.5 }
              : {}),
          }}
        >
          <Box sx={{ width: 125, height: 125, position: 'relative' }}>
            <Box component="svg" viewBox="0 0 100 100" sx={{ width: '100%', height: '100%', display: 'block' }} aria-hidden>
              <circle cx={50} cy={50} r={42} stroke="#e8edf2" strokeWidth={6} fill="none" />
              {ringSegmentsForPie.map((segment, index) => (
                <path
                  key={`macro-ring-segment-${index}`}
                  d={segment.path}
                  stroke={segment.color}
                  strokeWidth={6}
                  strokeLinecap="round"
                  fill="none"
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isPdfTableStyle ? 'flex-start' : 'center',
              gap: 0.25,
              width: isPdfTableStyle ? 'auto' : '100%',
              minWidth: isPdfTableStyle ? 160 : undefined,
            }}
          >
            {macroPieLegendItems.map((item) => (
              <Typography
                key={item.key}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '16px',
                  lineHeight: '26.3px',
                  textAlign: isPdfTableStyle ? 'left' : 'center',
                  color: item.color,
                }}
              >
                {formatMetric(item.percent, 1)}% {item.label}
              </Typography>
            ))}
          </Box>
        </Box>
      </Box>

      {viewMode === 0 ? (
        <Box
          sx={{
            borderTop: '1px solid #f3f4f6',
            pt: '21px',
            px: { xs: 0, sm: '24px' },
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%',
            minWidth: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: '12px',
                lineHeight: '13.5px',
                letterSpacing: '1.07px',
                textTransform: 'uppercase',
                color: '#99a1af',
              }}
            >
              Macros by Meal
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', width: { xs: 'auto', md: 280 } }}>
              {[...MACRO_LEGEND_FIGMA, { key: 'fibre', label: 'Fibre', color: MACRO_FIBRE_COLOR }].map((item) => (
                <Box
                  key={`meal-legend-${item.key}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: '6px', bgcolor: item.color, flexShrink: 0, mr: '6px' }} />
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '15px',
                      letterSpacing: '0.12px',
                      color: '#99a1af',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Box sx={{ width: 8, ml: '6px', flexShrink: 0 }} />
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {analysisData.mealMacros.map((meal) => (
              <Box
                key={meal.name}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: '16px',
                  width: '100%',
                }}
              >
                <Box sx={{ width: { md: 130 }, flexShrink: 0, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                      fontSize: '12px',
                      lineHeight: '16px',
                      color: '#364153',
                      textTransform: 'capitalize',
                    }}
                  >
                    {meal.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '15px',
                      letterSpacing: '0.12px',
                      color: '#99a1af',
                    }}
                  >
                    {formatMetric(meal.kcal, 0)} kcal
                  </Typography>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 14,
                    borderRadius: 9999,
                    overflow: 'hidden',
                    display: 'flex',
                    gap: '1px',
                    bgcolor: '#e5e7eb',
                  }}
                >
                  <Tooltip title={`Carbs — ${formatMetric(meal.carbs, 0)}%`} placement="top" arrow enterDelay={200}>
                    <Box sx={{ width: `${meal.carbs}%`, minWidth: 0, height: '100%', bgcolor: macroAnalysisFigmaSegmentColors.carbs }} />
                  </Tooltip>
                  <Tooltip title={`Protein — ${formatMetric(meal.protein, 0)}%`} placement="top" arrow enterDelay={200}>
                    <Box sx={{ width: `${meal.protein}%`, minWidth: 0, height: '100%', bgcolor: macroAnalysisFigmaSegmentColors.protein }} />
                  </Tooltip>
                  <Tooltip title={`Fat — ${formatMetric(meal.fat, 0)}%`} placement="top" arrow enterDelay={200}>
                    <Box sx={{ width: `${meal.fat}%`, minWidth: 0, height: '100%', bgcolor: macroAnalysisFigmaSegmentColors.fat }} />
                  </Tooltip>
                  <Tooltip title={`Fibre — ${formatMetric(meal.fibre, 0)}%`} placement="top" arrow enterDelay={200}>
                    <Box sx={{ width: `${meal.fibre}%`, minWidth: 0, height: '100%', bgcolor: MACRO_FIBRE_COLOR }} />
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    alignItems: 'center',
                    flexShrink: 0,
                    width: { xs: 'auto', md: 280 },
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '15px',
                        letterSpacing: '0.12px',
                        color: macroAnalysisFigmaSegmentColors.carbs,
                      }}
                    >
                      {meal.carbs}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '15px',
                        letterSpacing: '0.12px',
                        color: macroAnalysisFigmaSegmentColors.protein,
                      }}
                    >
                      {meal.protein}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '15px',
                        letterSpacing: '0.12px',
                        color: macroAnalysisFigmaSegmentColors.fat,
                      }}
                    >
                      {meal.fat}%
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 700,
                        fontSize: '12px',
                        lineHeight: '15px',
                        letterSpacing: '0.12px',
                        color: MACRO_FIBRE_COLOR,
                      }}
                    >
                      {meal.fibre}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};
